import React, { useCallback, useState } from 'react'

import Image from 'next/image'

import { SectionWrapper, VideoPlayer } from 'Components/Blocks'
import { Column, Text } from 'Components/UI'

import { VideoCoverImage } from 'Assets/Image'
import { ColumnsGlyph, PlayGlyph } from 'Assets/Svg/Icons'

import {
  AudienceItem,
  AudienceList,
  Content,
  ContentArea,
  CoverHolder,
  PauseButton,
  PlayButton,
  Strong,
  VideoArea,
} from './styles'

// The verticals actually delivered into. Real estate and insurance are
// deliberately absent — those were consultancy and research engagements, not
// shipped products, so they stay referral relationships rather than claimed
// vertical depth.
const AUDIENCE_ITEMS = [
  {
    id: 'construction',
    title: 'Construction and trades',
    description:
      'Project cost control, dockets, variations, field data capture.',
  },
  {
    id: 'automotive',
    title: 'Automotive dealer groups',
    description:
      'AI on the sales floor — customer context, follow-up, and real pipeline visibility for managers.',
  },
  {
    id: 'healthcare',
    title: 'Healthcare and multi-site operators',
    description:
      'Intake, records, scheduling, and the admin sitting between systems.',
  },
  {
    id: 'workforce',
    title: 'Workforce-heavy operations',
    description:
      'Scheduling, shift tracking, availability, location-aware clock-ins.',
  },
  {
    id: 'saas',
    title: 'SaaS and vertical software builders',
    description:
      'Platforms that have to hold up well past the MVP that got them here.',
  },
] as const

// The video sits in the `auto` track of a `427px auto` grid (40px gap) inside
// the 1440px content column, which itself carries 40px inline padding.
// Without this a `fill` image asks for 100vw and lands on the 1920w candidate.
const VIDEO_COVER_SIZES =
  '(min-width: 1520px) 973px, (min-width: 992px) calc(100vw - 547px), 100vw'

type Props = {
  videoFullUrl?: string
}

function BuiltForProductTeamsSection({ videoFullUrl }: Props) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [wasVideoStarted, setWasVideoStarted] = useState(false)

  const playVideo = useCallback(() => {
    setWasVideoStarted(true)
    setIsVideoPlaying(true)
  }, [])

  return (
    <SectionWrapper asCard py="120px">
      <Content>
        <ContentArea>
          <Column gap={6}>
            <Text as="h2" semiLight title title40>
              <Strong>Built for the businesses</Strong> that run on operations.
            </Text>

            <Text body body16 semiLight>
              If the work runs on dockets, shifts, intake or pipeline — and that
              process still runs on spreadsheets — that&apos;s what we build.
            </Text>
          </Column>

          <Column gap={6}>
            <Text body body16 medium>
              Who we build for:
            </Text>

            <AudienceList>
              {AUDIENCE_ITEMS.map(({ id, title, description }) => (
                <AudienceItem key={id}>
                  <Text as="h3" body16 medium title>
                    {title}
                  </Text>

                  <Text body body14 semiLight>
                    {description}
                  </Text>
                </AudienceItem>
              ))}
            </AudienceList>
          </Column>
        </ContentArea>

        <VideoArea>
          <CoverHolder
            animate={{ opacity: isVideoPlaying || wasVideoStarted ? 0 : 1 }}
            initial={{ opacity: 1 }}
          >
            <Image
              alt="Mathieu Diab, CEO of WorkAxle in Montreal, sitting indoors with a relaxed posture, wearing a black T-shirt with humorous engineer definition text, with a blurred office background"
              fill
              sizes={VIDEO_COVER_SIZES}
              src={VideoCoverImage}
            />
          </CoverHolder>

          <VideoPlayer
            playing={isVideoPlaying}
            src={videoFullUrl}
            onClick={() => setIsVideoPlaying(false)}
          />

          <PlayButton
            animate={{ opacity: isVideoPlaying ? 0 : 1 }}
            aria-label="Play the video"
            initial={{ opacity: 1 }}
            style={{
              cursor: isVideoPlaying ? 'default' : 'pointer',
            }}
            onClick={playVideo}
          >
            {PlayGlyph()}
          </PlayButton>

          <PauseButton
            animate={{ opacity: isVideoPlaying ? 1 : 0 }}
            aria-label="Pause the video"
            initial={{ opacity: 0 }}
            style={{
              cursor: !isVideoPlaying ? 'default' : 'pointer',
            }}
            onClick={() => setIsVideoPlaying(prevState => !prevState)}
          >
            {ColumnsGlyph()}
          </PauseButton>
        </VideoArea>
      </Content>
    </SectionWrapper>
  )
}

export default BuiltForProductTeamsSection
